import { cn } from '@goaltree/ui/lib/utils'
import { BookOpen } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const INTERVAL = 6000
const FADE = 400

type Fact = { text: string; source: string }

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

// Research-backed habit/goal facts shown while a plan generates. Starts at a random
// fact, rotates every few seconds, pauses on hover/focus, click advances.
export function LoadingFacts({ className }: { className?: string }) {
  const { t } = useTranslation()
  const raw = t('loadingFacts.items', { returnObjects: true })
  const facts = Array.isArray(raw) ? (raw as Fact[]) : []
  const count = facts.length
  const [index, setIndex] = useState(() => Math.floor(Math.random() * Math.max(count, 1)))
  const [visible, setVisible] = useState(true)
  const [paused, setPaused] = useState(false)
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function advance() {
    if (count < 2) return
    if (fadeTimer.current) clearTimeout(fadeTimer.current)
    if (prefersReducedMotion()) {
      setIndex((i) => (i + 1) % count)
      return
    }
    setVisible(false)
    fadeTimer.current = setTimeout(() => {
      setIndex((i) => (i + 1) % count)
      setVisible(true)
    }, FADE)
  }

  useEffect(() => {
    if (paused || count < 2) return
    const id = setInterval(advance, INTERVAL)
    return () => clearInterval(id)
  }, [paused, count])

  useEffect(
    () => () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current)
    },
    [],
  )

  if (!count) return null
  const current = index % count

  return (
    <button
      type="button"
      onClick={advance}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      title={t('loadingFacts.next')}
      className={cn(
        'w-full max-w-sm cursor-pointer rounded-2xl border border-border/60 bg-card/60 px-4 py-3 text-left transition-colors hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
    >
      <span className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        <BookOpen className="size-3" />
        {t('loadingFacts.label')}
      </span>
      <span
        className="grid transition-opacity duration-[400ms] ease-out motion-reduce:transition-none"
        style={{ opacity: visible ? 1 : 0 }}
      >
        {facts.map((f, i) => (
          <span
            key={i}
            className={cn('[grid-area:1/1] flex flex-col', i !== current && 'invisible')}
          >
            <span className="text-sm leading-snug text-foreground/90">{f.text}</span>
            <span className="mt-1.5 text-[11px] leading-tight text-muted-foreground">
              {f.source}
            </span>
          </span>
        ))}
      </span>
    </button>
  )
}
